import axios from 'axios';

const BACKEND_URL = window.location.origin;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth header
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('session_token');
      // Only redirect if not already on a public page.
      // Public pages: landing, login, forms (embed + soumission), legal, mobile login.
      const path = window.location.pathname;
      const isPublic =
        path === '/' ||
        path.startsWith('/login') ||
        path.startsWith('/signup') ||
        path.startsWith('/forgot-password') ||
        path.startsWith('/reset-password') ||
        path.startsWith('/soumission') ||
        path.startsWith('/embed') ||
        path.startsWith('/fr/conditions-dutilisation') ||
        path.startsWith('/en/terms-of-use') ||
        path.startsWith('/fr/politique-de-confidentialite') ||
        path.startsWith('/en/privacy-policy') ||
        path === '/mobile';
      if (!isPublic) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  // Self-serve signup. Accepts a single `name` (split into first/last for the
  // backend), `company_name`, and an optional `plan` (mapped to `plan_id`).
  signup: ({ email, password, name, company_name, plan, primary_domain }) => {
    const parts = (name || '').trim().split(/\s+/);
    const first_name = parts.shift() || '';
    const last_name = parts.join(' ') || first_name; // backend requires both
    return api.post('/auth/signup', {
      email,
      password,
      first_name,
      last_name,
      company_name,
      plan_id: plan || null,
      primary_domain: primary_domain || null,
    });
  },
  exchangeSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  // Password reset — public flow. The backend always returns 200 with a
  // generic message regardless of whether the email exists / is Google-only
  // / is inactive (anti-enumeration). Callers MUST surface the same toast
  // no matter the outcome.
  forgotPassword: ({ email }) => api.post('/auth/forgot-password', { email }),
  resetPassword: ({ token, new_password }) =>
    api.post('/auth/reset-password', { token, new_password }),
  verifyResetToken: (token) =>
    api.get(`/auth/reset-password/verify`, { params: { token } }),
};

// Admin API (Platform-Admin scoped operations)
export const adminAPI = {
  sendResetLink: (userId) => api.post(`/admin/clients/${userId}/send-reset-link`),
};

// Workspace API
export const workspaceAPI = {
  create: (data) => api.post('/workspaces', data),
  get: () => api.get('/workspaces'),
  update: (data) => api.put('/workspaces', data),
  getMembers: () => api.get('/workspaces/members'),
};

// Form API
export const formAPI = {
  create: (data) => api.post('/forms', data),
  list: () => api.get('/forms'),
  get: (formId) => api.get(`/forms/${formId}`),
  update: (formId, data) => api.put(`/forms/${formId}`, data),
  delete: (formId) => api.delete(`/forms/${formId}`),
};

// Lead API
export const leadAPI = {
  list: (params) => api.get('/leads', { params }),
  getPipeline: (params) => api.get('/leads/pipeline', { params }),
  getStats: () => api.get('/leads/stats'),
  get: (leadId) => api.get(`/leads/${leadId}`),
  update: (leadId, data) => api.put(`/leads/${leadId}`, data),
  delete: (leadId) => api.delete(`/leads/${leadId}`),
  addNote: (leadId, content) => api.post(`/leads/${leadId}/notes`, { content }),
  export: () => api.get('/leads/export/csv', { responseType: 'blob' }),
};

// Landing Page API
export const landingAPI = {
  create: (data) => api.post('/landing-pages', data),
  list: () => api.get('/landing-pages'),
  get: (pageId) => api.get(`/landing-pages/${pageId}`),
  update: (pageId, data) => api.put(`/landing-pages/${pageId}`, data),
  delete: (pageId) => api.delete(`/landing-pages/${pageId}`),
};

// Public API (no auth required)
export const publicAPI = {
  getForm: (formSlug) => api.get(`/public/form/${formSlug}`),
  submitForm: (formSlug, data, tracking) =>
    api.post(`/public/form/${formSlug}/submit`, { data, tracking }),
  // AI Estimation wizard submission — payload carries structured answers,
  // optional base64 photos and tracking. Backend branches on form_type.
  submitAiEstimation: (formSlug, { answers, photos, tracking, honeypot }) =>
    api.post(`/public/form/${formSlug}/submit`, { data: { _honeypot: honeypot || '' }, answers, photos, tracking }, {
      timeout: 120000, // photos can be heavy — generous client-side timeout
    }),
  getLandingPage: (workspaceSlug, pageSlug) =>
    api.get(`/public/page/${workspaceSlug}/${pageSlug}`),
  // Wizard funnel tracking — fire-and-forget, short timeout so it can
  // never slow down the public form.
  trackWizardEvent: (payload) =>
    api.post('/public/wizard-track', payload, { timeout: 5000 }),
};

// Wizard analytics (admin) — AI estimation funnel stats
export const wizardStatsAPI = {
  get: () => api.get('/wizard-stats'),
};

// Billing API (Stripe Checkout — auth required)
// Backend resolves the Stripe price ID from plan_id + interval; the frontend
// never carries the Stripe IDs directly.
export const billingAPI = {
  createCheckout: ({ plan_id, interval }) =>
    api.post('/billing/checkout', { plan_id, interval }),
};

export default api;
